import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DEFAULT_RELEASES, ReleaseItem, API_UPDATE_HISTORY_URL } from '../config/releases';

const DownloadPage: React.FC = () => {
  const { t } = useTranslation();
  const [releases, setReleases] = useState<ReleaseItem[]>(DEFAULT_RELEASES);

  // 静默从 fakey 获取最新全量版本信息（走 Cloudflare CDN 边缘缓存）
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API_UPDATE_HISTORY_URL}?t=${Date.now()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;

        const json = await res.json() as { success: boolean; data?: { releases?: ReleaseItem[] } };
        if (isMounted && json?.success && Array.isArray(json.data?.releases) && json.data.releases.length > 0) {
          setReleases(json.data.releases);
        }
      } catch {
        // 网络异常或离线环境，平滑保持 DEFAULT_RELEASES 兜底，绝不白屏
      }
    };

    fetchHistory();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const latest = releases[0] || DEFAULT_RELEASES[0];
  const historyReleases = releases.slice(1);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-16 md:py-24 flex items-center justify-center px-4">
      <div className="max-w-3xl w-full text-center">
        {/* Animated Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider mb-6 border border-emerald-200 dark:border-emerald-800/60">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span>v{latest.version} {t('download.currentBadge', '最新稳定版')} ({latest.release_date})</span>
        </div>

        {/* Headings */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-4">
          {t('download.title', '下载 AtlasSlicer 2D 桌面客户端')}
        </h1>
        <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8 max-w-xl mx-auto">
          {t('download.description')}
        </p>

        {/* ========================================================= */}
        {/* 🌟 核心区：最新版本多格式下载卡片组 */}
        {/* ========================================================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-left">
          {/* Windows 平台卡片：同时提供【安装包】与【绿色包】 */}
          <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 border-2 border-orange-500 shadow-xl flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                  <svg className="w-5 h-5 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.949-1.801"/>
                  </svg>
                  Windows (64-bit)
                </span>
                <span className="text-xs font-bold text-orange-600 bg-orange-100 dark:bg-orange-950 dark:text-orange-400 px-2.5 py-0.5 rounded-full">
                  {t('download.recommendedTag', '推荐安装')}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {t('download.winPlatformDesc', '支持 Windows 10 / 11 64位系统。纯本地离线计算，GPU 硬解加速。')}
              </p>
            </div>

            <div className="space-y-2.5 mt-2">
              {/* 1. Windows 安装包 (.exe) */}
              <a
                href={latest.downloads?.installer || '#'}
                download
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm text-center shadow-md transition-all active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>{t('download.downloadWinInstaller', '下载 Windows 安装包 (.exe)')}</span>
              </a>

              {/* 2. Windows 绿色免安装包 (.zip) */}
              <a
                href={latest.downloads?.portable || '#'}
                download
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-slate-100 dark:bg-gray-700/70 hover:bg-slate-200 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-200 font-semibold text-xs text-center border border-slate-200 dark:border-gray-600 transition-all active:scale-95"
              >
                <svg className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span>{t('download.downloadWinPortable', '下载 Windows 绿色便携版 (.zip)')}</span>
              </a>

              {/* Windows SmartScreen 快速提示 */}
              <div className="pt-1 text-[11px] text-gray-500 dark:text-gray-400 flex items-start gap-1 leading-tight">
                <span className="text-amber-500 font-bold shrink-0">💡</span>
                <span>{t('download.smartScreenTip', '若提示「未知开发者」：点击【更多信息】➜【仍要运行】即可正常使用')}</span>
              </div>
            </div>
          </div>

          {/* macOS 平台卡片 */}
          <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.63-.76 1.05-1.83.93-2.89-.91.04-2.02.61-2.67 1.37-.58.67-.99 1.76-.85 2.8 1.02.08 2.05-.52 2.59-1.28"/>
                  </svg>
                  macOS
                </span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-400 px-2.5 py-0.5 rounded-full">
                  {t('download.nativeTag', '原生架构')}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {t('download.macPlatformDesc', '支持 Apple Silicon (M1/M2/M3/M4) 与 Intel 架构 Mac 设备。')}
              </p>
            </div>

            <div className="space-y-2.5 mt-2">
              <a
                href={latest.downloads?.mac || '#'}
                download
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-bold text-sm text-center shadow-md transition-all active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>{t('download.downloadMacDmg', '下载 macOS 客户端 (.dmg)')}</span>
              </a>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 📜 历史版本下载列表 */}
        {/* ========================================================= */}
        {historyReleases.length > 0 && (
          <div className="mb-10 text-left">
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('download.historyTitle', '历史版本下载')}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('download.historyDesc', '可按需回退下载旧版本')}
              </span>
            </div>

            <div className="space-y-2.5">
              {historyReleases.map((rel) => (
                <div
                  key={rel.version}
                  className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-900 dark:text-white">
                        v{rel.version}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {rel.release_date}
                      </span>
                    </div>

                    {/* 历史版本的 3 种格式轻量下载按钮 */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {rel.downloads?.installer && (
                        <a
                          href={rel.downloads.installer}
                          download
                          className="px-2.5 py-1 rounded-lg bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-900/60 text-xs font-semibold hover:bg-orange-100 transition-colors"
                          title={t('download.downloadWinInstaller', '下载 Windows 安装包 (.exe)')}
                        >
                          {t('download.historyInstaller', '安装包 (.exe)')}
                        </a>
                      )}
                      {rel.downloads?.portable && (
                        <a
                          href={rel.downloads.portable}
                          download
                          className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-600 text-xs font-medium hover:bg-slate-200 transition-colors"
                          title={t('download.downloadWinPortable', '下载 Windows 绿色便携版 (.zip)')}
                        >
                          {t('download.historyPortable', '绿色包 (.zip)')}
                        </a>
                      )}
                      {rel.downloads?.mac && (
                        <a
                          href={rel.downloads.mac}
                          download
                          className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-600 text-xs font-medium hover:bg-slate-200 transition-colors"
                          title={t('download.downloadMacDmg', '下载 macOS 客户端 (.dmg)')}
                        >
                          {t('download.historyMac', 'macOS (.dmg)')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 安全与安装说明（Windows 未知发布者提示） */}
        <div className="mb-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/25 border border-amber-200 dark:border-amber-900/40 text-xs text-gray-700 dark:text-gray-300 text-left">
          <div className="font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1.5">
            <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>{t('download.securityTitle', '安全与安装说明（关于 Windows 未知发布者提示）')}</span>
          </div>
          <p className="mb-2 text-slate-600 dark:text-slate-300 leading-relaxed">
            {t('download.securityDesc')}
          </p>
          <div className="space-y-1.5 bg-white/70 dark:bg-gray-800/60 p-2.5 rounded-lg border border-amber-100 dark:border-amber-900/30 text-slate-700 dark:text-slate-300">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
              <span>{t('download.securityStep1')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
              <span>{t('download.securityStep2')}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-[11px] pt-0.5">
              <span className="w-4 h-4 rounded-full bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200 flex items-center justify-center text-[10px] font-bold shrink-0">💡</span>
              <span>{t('download.securityStep3')}</span>
            </div>
          </div>
        </div>

        {/* 激活提示小贴士 */}
        <div className="mb-8 p-4 rounded-xl bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/40 text-xs text-gray-600 dark:text-gray-300 text-left">
          <div className="font-semibold text-orange-700 dark:text-orange-400 mb-1 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{t('download.licenseTitle', '激活说明')}</span>
          </div>
          {t('download.licenseNote')}
        </div>

        {/* 底部 CTA 按钮 */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/pricing"
            className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm shadow-lg shadow-orange-500/25 transition-all transform active:scale-95"
          >
            {t('download.getLicense', '获取终身授权')}
          </Link>
          <Link
            to="/"
            className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-sm border border-gray-200 dark:border-gray-700 transition-all"
          >
            {t('download.backHome', '返回首页')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
